import React, { useState } from "react";
import { gettext } from "../../shared/utils/legacy";
import { useMutate } from "restful-react";
import useCachedUrlData from "../../shared/hooks/useCachedUrlData";
import useCroprBackbone from "../../shared/hooks/useCroprBackbone";
import cropr, { RecordModel } from "../../shared/utils/cropr";
import GeometryPartPreview from "../../shared/components/GeometryPartPreview";

const RainDancerStatus = () => {
  const { data: getData, error: getError } =
    useCachedUrlData<RainDancerApiData>("/apps/raindancer/data/");
  const [updates, setUpdates] = useState<{ data: RainDancerApiData | null; error: any }>({
    data: null,
    error: null,
  });
  const data = updates.data || getData;
  const error = updates.error || getError;

  const { mutate: post, loading } = useMutate<RainDancerApiData>("POST", "/apps/raindancer/data/");
  const [, recordsCollection] = useCroprBackbone<RecordModel>("records");
  if (error) {
    return (
      <div>
        <h3 className="mb-3">Error</h3>
        <p>
          {gettext("Did you install the app?")}{" "}
          <a href="/cropr/settings/apps/#raindancer">{gettext("Install application")}</a>
        </p>
      </div>
    );
  }
  if (!data) {
    return (
      <div>
        <i className="fa fa-sync fa-spin" /> Loading...
      </div>
    );
  }
  const { records, operations } = data;
  return (
    <>
      <h3 className="mb-3">Raindancer</h3>
      <h2>{gettext("New data")}</h2>
      {operations.length ? (
        <div className="clearfix">
          <p>
            {gettext("New records available at Raindancer")}: {operations.length}
            <button
              className="btn btn-success"
              disabled={loading}
              style={{ marginLeft: "10px" }}
              onClick={() => {
                post({}, { headers: { "X-CSRFToken": cropr.getCookie("csrftoken") } }).then(
                  ({ updates, ...data }) => {
                    for (let [key, values] of Object.entries(updates || {})) {
                      // @ts-ignore
                      const collection: Backbone.Collection = cropr[key];
                      // @ts-ignore
                      collection.add(values);
                    }
                    setUpdates({ data: data, error: null });
                  }
                );
              }}
            >
              <i className={`fa fa-sync${loading ? " fa-spin" : ""}`} /> {gettext("Make records")}
            </button>
          </p>
          <table className="table table-striped table-valign-middle">
            <thead>
              <tr>
                <th>{gettext("Date")}</th>
                <th className="text-right">mm</th>
                <th className="text-right">m³</th>
                <th>{gettext("Notes")}</th>
              </tr>
            </thead>
            <tbody>
              {operations.map((rec: any, index) => (
                <tr key={index}>
                  <td>
                    {rec.start.replace("T", " ")}{" "}
                    {!(rec.finished && rec.totalVolume) && (
                      <i className="fal fa-circle-notch fa-spin" />
                    )}
                  </td>
                  <td align="right">{rec.precipitation?.toFixed(2)}</td>
                  <td align="right">{rec.totalVolume?.toFixed(2)}</td>
                  <td>{rec.notes}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ) : (
        <div className="clearfix">{gettext("No new records available")}</div>
      )}
      <hr />
      <h2>{gettext("Records")}</h2>
      <div className="clearfix">
        <table className="table table-striped table-valign-middle">
          <thead>
            <tr>
              <th>{gettext("Date")}</th>
              <th>{gettext("Cropfield")}</th>
              <th className="text-right">{gettext("Worked area")}</th>
              <th className="text-right">{gettext("mm")}</th>
              <th className="text-right">m³</th>
            </tr>
          </thead>
          <tbody>
            {records.map((r: any, index) => {
              const rec = recordsCollection.get(r.id);
              const cropfield = rec?.getRelated("cropfield");
              return (
                <tr key={index} className={rec ? "" : "table-success"}>
                  <td>{rec?.get("date") || r.data.start}</td>
                  <td>{cropfield?.get("name") || "?"}</td>
                  <td align="right">
                    {rec && cropfield && (
                      <>
                        {(
                          (rec.get("worked_area") / cropfield.get("registered_area")) *
                          100
                        ).toFixed(0)}
                        {"% "}
                        <GeometryPartPreview
                          size={40}
                          geometry={cropfield.get("geometry")}
                          overlays={[rec.get("part_field")]}
                        />{" "}
                        {rec.getAreaString()}
                      </>
                    )}
                  </td>
                  <td align="right">
                    {(rec?.getValueFromActivity(1000900) || r.data.precipitation).toFixed(2)}
                  </td>
                  <td align="right">{r.data.totalVolume}</td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </>
  );
};

interface RainDancerApiData {
  records: Array<[number, any]>;
  operations: Array<any>;
  installation: any;
  updates?: any;
}

export default RainDancerStatus;
